import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { utcStringToLocalDate } from '../utils/timezone'
import '../style/components/CalendarDaySchedule.css'

type CalendarDayScheduleProps = {
  baseDate: Date
  items: Array<{
    id: string
    title?: string
    start?: string
    end?: string
    date_created?: string
    tags?: string[] | string | null
  }>
  onSelect?: (id: string, rect?: DOMRect) => void
  onCreateRequest?: (start: Date, anchor: { x: number; y: number }) => void
  showHeader?: boolean
  headerLabel?: string
  variant?: 'default' | 'plain'
}

type CalendarItem = CalendarDayScheduleProps['items'][number]

const MINUTE_HEIGHT = 2
const HOUR_HEIGHT = 60 * MINUTE_HEIGHT
const DAY_HEIGHT = 25 * 60 * MINUTE_HEIGHT
const STACK_GAP_MINUTES = 60
const BLOCK_GAP = 0
const MIN_BLOCK_WIDTH = 90
const HOVER_EXPAND_RATIO = 0.4
const MIN_DURATION_MINUTES = 20

const toMinutes = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return 0
  return date.getHours() * 60 + date.getMinutes()
}

const formatTime = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return value || '—'
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

const getTagTheme = (tags?: string[] | string | null) => {
  const list = Array.isArray(tags) ? tags : typeof tags === 'string' ? [tags] : []
  const normalized = list.map((tag) => tag.toLowerCase())
  const isGoogle = normalized.some((tag) => tag.includes('google'))
  const isAzure = normalized.some((tag) => tag.includes('azure'))
  const isOutlook = normalized.some((tag) => tag.includes('outlook'))
  const isReminder = normalized.some((tag) => tag.includes('reminder'))
  const isMeeting = normalized.some((tag) => tag.includes('meeting'))
  if (isGoogle) {
    return {
      gradient:
        'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.3) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.25) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.22) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.28) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(135deg, rgba(120, 173, 251, 0.18), rgba(246, 167, 157, 0.16), rgba(255, 226, 137, 0.16), rgba(108, 200, 137, 0.18))',
      gradientHover:
        'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.4) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.35) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.32) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.38) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(135deg, rgba(120, 173, 251, 0.28), rgba(246, 167, 157, 0.25), rgba(255, 226, 137, 0.26), rgba(108, 200, 137, 0.28))',
      border: 'rgba(123, 170, 247, 0.45)',
      borderHover: 'rgba(123, 170, 247, 0.65)',
      shadow: '0 10px 26px rgba(93, 141, 220, 0.22)',
      shadowHover: '0 16px 32px rgba(93, 141, 220, 0.3)',
      glow:
        'linear-gradient(90deg, rgba(120, 173, 251, 0.3), rgba(246, 167, 157, 0.22), rgba(255, 226, 137, 0.2), rgba(108, 200, 137, 0.22))',
      bar: 'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.86) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.72) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.68) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.76) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(180deg, #78adfb 0%, #f6a79d 38%, #ffe289 68%, #6cc889 100%)',
      time: '#4285F4',
      pill:
        'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.86) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.72) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.68) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.76) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(135deg, #78adfb 0%, #f6a79d 38%, #ffe289 68%, #6cc889 100%)',
    }
  }
  if (isAzure || isOutlook) {
    return {
      gradient: 'linear-gradient(135deg, rgba(3, 79, 160, 0.18), rgba(0, 120, 212, 0.18), rgba(36, 158, 228, 0.18))',
      gradientHover: 'linear-gradient(135deg, rgba(3, 79, 160, 0.3), rgba(0, 120, 212, 0.3), rgba(36, 158, 228, 0.3))',
      border: 'rgba(0, 120, 212, 0.5)',
      borderHover: 'rgba(0, 120, 212, 0.7)',
      shadow: '0 10px 26px rgba(0, 120, 212, 0.22)',
      shadowHover: '0 16px 32px rgba(0, 120, 212, 0.32)',
      glow: 'linear-gradient(90deg, rgba(3, 79, 160, 0.35), rgba(0, 120, 212, 0.18))',
      bar: 'linear-gradient(180deg, #034fa0, #0078d4, #249ee4)',
      time: '#0078d4',
      pill: 'linear-gradient(135deg, #034fa0, #0078d4, #249ee4)',
    }
  }
  if (isReminder) {
    return {
      gradient: 'linear-gradient(135deg, rgba(43, 182, 115, 0.2), rgba(154, 230, 180, 0.2))',
      gradientHover: 'linear-gradient(135deg, rgba(43, 182, 115, 0.35), rgba(154, 230, 180, 0.35))',
      border: 'rgba(43, 182, 115, 0.5)',
      borderHover: 'rgba(43, 182, 115, 0.7)',
      shadow: '0 10px 26px rgba(43, 182, 115, 0.22)',
      shadowHover: '0 16px 32px rgba(43, 182, 115, 0.32)',
      glow: 'linear-gradient(90deg, rgba(43, 182, 115, 0.35), rgba(154, 230, 180, 0.18))',
      bar: 'linear-gradient(180deg, #2bb673, #9ae6b4)',
      time: '#1e9a60',
      pill: 'linear-gradient(135deg, #2bb673, #9ae6b4)',
    }
  }
  if (isMeeting) {
    return {
      gradient: 'linear-gradient(135deg, rgba(224, 88, 88, 0.2), rgba(246, 163, 106, 0.2))',
      gradientHover: 'linear-gradient(135deg, rgba(224, 88, 88, 0.35), rgba(246, 163, 106, 0.35))',
      border: 'rgba(224, 88, 88, 0.5)',
      borderHover: 'rgba(224, 88, 88, 0.7)',
      shadow: '0 10px 26px rgba(224, 88, 88, 0.25)',
      shadowHover: '0 16px 32px rgba(224, 88, 88, 0.35)',
      glow: 'linear-gradient(90deg, rgba(224, 88, 88, 0.35), rgba(246, 163, 106, 0.18))',
      bar: 'linear-gradient(180deg, #e05858, #f6a36a)',
      time: '#e05858',
      pill: 'linear-gradient(135deg, #e05858, #f6a36a)',
    }
  }
  return {
    gradient: 'linear-gradient(135deg, rgba(100, 129, 210, 0.18), rgba(120, 242, 200, 0.18))',
    gradientHover: 'linear-gradient(135deg, rgba(100, 129, 210, 0.3), rgba(120, 242, 200, 0.3))',
    border: 'rgba(100, 129, 210, 0.45)',
    borderHover: 'rgba(100, 129, 210, 0.65)',
    shadow: '0 10px 26px rgba(100, 129, 210, 0.22)',
    shadowHover: '0 16px 32px rgba(100, 129, 210, 0.35)',
    glow: 'linear-gradient(90deg, rgba(100, 129, 210, 0.35), rgba(120, 242, 200, 0.18))',
    bar: 'linear-gradient(180deg, #6481d2, #78f2c8)',
    time: '#6481d2',
    pill: 'linear-gradient(135deg, #6481d2, #78f2c8)',
  }
}

const sections = [0, 1]

function CalendarDaySchedule({
  baseDate,
  items,
  onSelect,
  onCreateRequest,
  showHeader = true,
  headerLabel = 'Today',
  variant = 'default',
}: CalendarDayScheduleProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const gridRef = useRef<HTMLDivElement | null>(null)
  const [gridWidth, setGridWidth] = useState<number | null>(null)
  const [now, setNow] = useState(new Date())

  // Update "now" every minute to move the current time line
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  const isToday = useMemo(() => {
    const d = new Date(baseDate)
    const today = new Date()
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    )
  }, [baseDate])

  const currentTimeTop = useMemo(() => {
    if (!isToday) return null
    const minutes = now.getHours() * 60 + now.getMinutes()
    // Remove the START_OFFSET_MINUTES to move the line up and match the grid accurately
    return minutes * MINUTE_HEIGHT
  }, [isToday, now])

  const sorted = useMemo(

    () =>
      items.toSorted(
        (a, b) => toMinutes(a.start || a.date_created) - toMinutes(b.start || b.date_created),
      ),
    [items],
  )

  const clusters = useMemo(() => {
    return sorted.reduce<CalendarItem[][]>((acc, item) => {
      const itemStart = toMinutes(item.start || item.date_created)
      const lastCluster = acc[acc.length - 1]
      if (!lastCluster) {
        acc.push([item])
        return acc
      }

      const clusterStart = toMinutes(lastCluster[0]?.start || lastCluster[0]?.date_created)
      const previousStart = toMinutes(
        lastCluster[lastCluster.length - 1]?.start ||
          lastCluster[lastCluster.length - 1]?.date_created,
      )
      const isHourFromClusterStart = itemStart - clusterStart >= STACK_GAP_MINUTES
      const isHourFromPrevious = itemStart - previousStart >= STACK_GAP_MINUTES

      if (isHourFromClusterStart || isHourFromPrevious) {
        acc.push([item])
      } else {
        lastCluster.push(item)
      }

      return acc
    }, [])
  }, [sorted])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const times = sorted
      .map((item) => toMinutes(item.start || item.date_created))
      .sort((a, b) => a - b)
    const median = times.length
      ? times[Math.floor(times.length / 2)]
      : 12 * 60
    const targetTop = median * MINUTE_HEIGHT
    const desired = DAY_HEIGHT + targetTop - el.clientHeight / 2
    const maxScroll = DAY_HEIGHT * 2 - el.clientHeight
    const clamped = Math.max(0, Math.min(desired, maxScroll))

    requestAnimationFrame(() => {
      el.scrollTop = clamped
    })
  }, [sorted])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const onScroll = () => {
      const total = DAY_HEIGHT * 2
      const buffer = DAY_HEIGHT * 0.25
      if (el.scrollTop < buffer) {
        el.scrollTop += DAY_HEIGHT
      } else if (el.scrollTop + el.clientHeight > total - buffer) {
        el.scrollTop -= DAY_HEIGHT
      }
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const updateWidth = () => {
      const width = gridRef.current?.clientWidth || null
      setGridWidth(width)
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const placedBySection: Record<number, Array<{ top: number; bottom: number; left: number; width: number }>> =
    {}

  return (
    <div
      className={`day-schedule ${variant === 'plain' ? 'plain' : ''}`}
      style={{ '--minute-scale': MINUTE_HEIGHT } as CSSProperties}
    >
      {showHeader && <div className="day-schedule-header">{headerLabel}</div>}
      <div
        className="day-scroll"
        ref={scrollRef}
        onClick={(e) => {
          if (!onCreateRequest) return
          const target = e.target as HTMLElement | null
          if (target?.closest('.call-block')) return
          const gridRect = gridRef.current?.getBoundingClientRect()
          if (!gridRect) return
          const y = e.clientY - gridRect.top
          const minutesFromTop = Math.max(0, Math.min(y / MINUTE_HEIGHT, 24 * 60))
          const snappedMinutes = Math.round(minutesFromTop / 30) * 30
          const start = new Date(baseDate)
          start.setHours(0, 0, 0, 0)
          start.setMinutes(snappedMinutes)
          const anchor = { x: e.clientX, y: e.clientY + window.scrollY }
          onCreateRequest(start, anchor)
        }}
      >
        <div className="hour-grid" ref={gridRef}>
          {isToday && currentTimeTop !== null && (
            <>
              {/* Line for both sections since it's a looping view */}
              <div className="week-grid-now-line" style={{ top: `${currentTimeTop}px`, left: '100px' }}>
                <span className="week-grid-now-dot" />
              </div>
              <div className="week-grid-now-line" style={{ top: `${currentTimeTop + DAY_HEIGHT}px`, left: '100px' }}>
                <span className="week-grid-now-dot" />
              </div>
            </>
          )}
          {sections.map((section) =>
            Array.from({ length: 25 }).map((_, index) => (
              <div
                key={`${section}-${index}`}
                className="hour-row"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="hour-label">
                  {index === 0
                    ? '12 AM'
                    : index === 12
                    ? 'Noon'
                    : index < 12
                    ? `${index} AM`
                    : index === 24
                    ? ''
                    : `${index - 12} PM`}
                </span>
                <span className="hour-line" />
              </div>
            )),
          )}
          {sections.map((section) =>
            clusters.map((cluster, clusterIndex) => {
              const labelSpace = 110 // space for hour labels and gutter
              const rightSpace = 16
              const availablePx =
                gridWidth && gridWidth > 0
                  ? gridWidth - (labelSpace + rightSpace + BLOCK_GAP * (cluster.length - 1))
                  : null
              const sharePx = availablePx && availablePx > 0 ? availablePx / cluster.length : null
              const available = `calc(100% - ${labelSpace + rightSpace + BLOCK_GAP * (cluster.length - 1)}px)`
              return cluster.map((item, itemIndex) => {
                const minutesStart = toMinutes(item.start || item.date_created)
                const minutesEnd = toMinutes(item.end || item.date_created)
                const duration = Math.max(MIN_DURATION_MINUTES, Math.max(0, minutesEnd - minutesStart))
                // Remove START_OFFSET_MINUTES to match the current-time-line and grid accurately
                const top = section * DAY_HEIGHT + minutesStart * MINUTE_HEIGHT
                const height = Math.max(duration, MIN_DURATION_MINUTES) * MINUTE_HEIGHT
                const title =
                  (item.title || '').replace(/^["']+|["']+$/g, '').trim() || 'Untitled conversation'
                const leftPx = sharePx
                  ? labelSpace + itemIndex * (sharePx + BLOCK_GAP)
                  : null
                const baseWidthPx = sharePx ? sharePx - BLOCK_GAP : null

                const placed = placedBySection[section] || []
                const overlapping = placed.filter(
                  (p) => !(p.bottom <= top || p.top >= top + height),
                )
                let obstacleLeft: number | null = null
                if (leftPx != null) {
                  overlapping.forEach((p) => {
                    if (p.left > leftPx && (obstacleLeft === null || p.left < obstacleLeft)) {
                      obstacleLeft = p.left
                    }
                  })
                }

                let widthPx = baseWidthPx && baseWidthPx > 0 ? baseWidthPx : null
                if (widthPx != null && obstacleLeft != null && leftPx != null) {
                  const maxWidth = obstacleLeft - leftPx - BLOCK_GAP
                  widthPx = Math.min(widthPx, maxWidth)
                }
                if (widthPx != null) {
                  widthPx = Math.max(MIN_BLOCK_WIDTH, widthPx)
                  if (obstacleLeft != null && leftPx != null) {
                    const maxWidth = obstacleLeft - leftPx - BLOCK_GAP
                    widthPx = Math.min(widthPx, maxWidth)
                  }
                  if (widthPx < 0) widthPx = 0
                }

                if (!placedBySection[section]) placedBySection[section] = []
                if (leftPx != null && widthPx != null) {
                  placedBySection[section].push({
                    top,
                    bottom: top + height,
                    left: leftPx,
                    width: widthPx,
                  })
                }

                const width =
                  leftPx != null && widthPx != null
                    ? `${widthPx}px`
                    : `calc(${available} * ${1 / cluster.length} - ${BLOCK_GAP}px)`
                const left =
                  leftPx != null
                    ? `${leftPx}px`
                    : `calc(${labelSpace}px + (${BLOCK_GAP * itemIndex}px) + (${available} * ${
                        itemIndex / cluster.length
                      }))`
                const hoverAvailablePx =
                  gridWidth != null ? gridWidth - labelSpace - rightSpace : null
                const baseWidthForHover = widthPx ?? baseWidthPx ?? sharePx ?? null
                let hoverWidthPx: number | null = null
                if (hoverAvailablePx != null) {
                  const target = hoverAvailablePx * HOVER_EXPAND_RATIO
                  hoverWidthPx = Math.max(baseWidthForHover ?? target, target)
                  hoverWidthPx = Math.min(hoverWidthPx, hoverAvailablePx)
                }
                const hoverWidth =
                  hoverWidthPx != null
                    ? `${hoverWidthPx}px`
                    : `calc((100% - ${labelSpace + rightSpace}px) * ${HOVER_EXPAND_RATIO})`
                let hoverLeftOffset: string | undefined
                if (hoverWidthPx != null && leftPx != null && gridWidth) {
                  const spaceRight = gridWidth - rightSpace - leftPx
                  const neededShift = Math.max(0, hoverWidthPx - spaceRight)
                  const maxShiftLeft = Math.max(0, leftPx - labelSpace)
                  const shift = Math.min(neededShift, maxShiftLeft)
                  if (shift > 0) hoverLeftOffset = `-${shift}px`
                }
                const theme = getTagTheme(item.tags)
                const style: CSSProperties & {
                  '--base-left'?: string
                  '--base-width'?: string
                  '--hover-width'?: string
                  '--hover-left-offset'?: string
                  '--hover-height'?: string
                  '--base-height'?: string
                  '--call-gradient'?: string
                  '--call-gradient-hover'?: string
                  '--call-border'?: string
                  '--call-border-hover'?: string
                  '--call-shadow'?: string
                  '--call-shadow-hover'?: string
                  '--call-glow'?: string
                  '--call-bar-gradient'?: string
                  '--call-time-color'?: string
                  '--call-pill-gradient'?: string
                } = {
                  top: `${top}px`,
                  left,
                  '--base-left': left,
                  '--base-width': width,
                  '--hover-width': hoverWidth,
                  '--hover-left-offset': hoverLeftOffset,
                  '--hover-height': `${Math.max(height, 30 * MINUTE_HEIGHT)}px`,
                  '--base-height': `${height}px`,
                  '--call-gradient': theme.gradient,
                  '--call-gradient-hover': theme.gradientHover,
                  '--call-border': theme.border,
                  '--call-border-hover': theme.borderHover,
                  '--call-shadow': theme.shadow,
                  '--call-shadow-hover': theme.shadowHover,
                  '--call-glow': theme.glow,
                  '--call-bar-gradient': theme.bar,
                  '--call-time-color': theme.time,
                  '--call-pill-gradient': theme.pill,
                }
                return (
                  <div
                    key={`${section}-${clusterIndex}-${item.id}`}
                    className="call-block clickable"
                    role="button"
                    tabIndex={0}
                    style={style}
                    onClick={(e) => onSelect?.(item.id, (e.currentTarget as HTMLElement).getBoundingClientRect())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelect?.(item.id, (e.currentTarget as HTMLElement).getBoundingClientRect())
                      }
                    }}
                  >
                    <div className="call-time">
                      {formatTime(item.start || item.date_created)} – {formatTime(item.end || item.date_created)}
                    </div>
                    <div className="call-title" title={title}>
                      {title}
                    </div>
                  </div>
                )
              })
            }),
          )}
        </div>
      </div>
    </div>
  )
}

export default CalendarDaySchedule
